import type { ChatMessage, Locale } from '@spot/shared';
import { useTranslation } from 'react-i18next';

import { Avatar } from '../../components/ui/avatar';
import { formatTime } from '../events/format';
import { usePersonName } from '../profile/person-link';
import type { PendingMessage } from './use-chat';

/** Text messages show their body; quick statuses (T-904) are worded in the viewer's language. */
export function useMessageText(): (message: Pick<ChatMessage, 'kind' | 'body'>) => string {
	const { t } = useTranslation();
	return (message) =>
		message.kind === 'status_arrived'
			? t('chat.status.arrived')
			: message.kind === 'status_late'
				? t('chat.status.late')
				: (message.body ?? '');
}

const bubble = 'text-body max-w-[80%] px-3 py-2 whitespace-pre-wrap break-words';

/** Text + time inside the bubble (bottom-right). Quick statuses carry their emoji in the text. */
function BubbleBody({ message, time }: { message: ChatMessage; time: string }) {
	const messageText = useMessageText();
	return (
		<>
			<span>{messageText(message)}</span>
			<span className="text-caption float-right mt-1 ml-2 translate-y-1 opacity-70">{time}</span>
		</>
	);
}

/** Corners: the group's last bubble gets the "tail" corner toward its sender. */
function corners(mine: boolean, first: boolean, last: boolean): string {
	// Full class names (Tailwind only sees literal strings).
	if (mine) {
		return `rounded-card ${first ? '' : 'rounded-tr-md'} ${last ? 'rounded-br-sm' : 'rounded-br-md'}`;
	}
	return `rounded-card ${first ? '' : 'rounded-tl-md'} ${last ? 'rounded-bl-sm' : 'rounded-bl-md'}`;
}

/**
 * One message (S11, T-306d): own on the right in `primary`, others on the left. In a group of
 * one sender's messages only the first shows the name and only the last the avatar.
 */
export function MessageBubble({
	message,
	timeZone,
	locale,
	first = true,
	last = true,
	onActions,
}: {
	message: ChatMessage;
	timeZone: string;
	locale: Locale;
	first?: boolean;
	last?: boolean;
	/** Others' messages: tap opens the message actions (report, T-801). */
	onActions?: () => void;
}) {
	const { t } = useTranslation();
	const nameOf = usePersonName();
	const time = formatTime(new Date(message.createdAt), timeZone, locale);
	const spacing = first ? 'mt-2' : 'mt-0.5';

	if (message.system !== null) {
		return (
			<li className="my-2 flex justify-center">
				<span className="bg-surface text-caption text-text-muted rounded-full px-3 py-1">
					{t('chat.joined', { name: nameOf(message.system.user.displayName) })}
				</span>
			</li>
		);
	}
	if (message.mine) {
		return (
			<li className={`flex justify-end ${spacing}`}>
				<p className={`${bubble} ${corners(true, first, last)} bg-primary text-text`}>
					<BubbleBody message={message} time={time} />
				</p>
			</li>
		);
	}
	const sender = message.sender;
	const look = `${bubble} ${corners(false, first, last)} bg-surface text-text`;
	return (
		<li className={`flex items-end gap-2 ${spacing}`}>
			{last ? (
				<Avatar
					name={sender?.displayName ?? null}
					photoUrl={sender?.photoUrl ?? null}
					size="size-8"
				/>
			) : (
				<span aria-hidden="true" className="size-8 shrink-0" />
			)}
			<div className="flex min-w-0 flex-1 flex-col items-start gap-0.5">
				{first && (
					<span className="text-caption text-text-muted truncate pl-1">
						{nameOf(sender?.displayName ?? null)}
					</span>
				)}
				{onActions === undefined ? (
					<p className={look}>
						<BubbleBody message={message} time={time} />
					</p>
				) : (
					<button
						type="button"
						aria-haspopup="dialog"
						onClick={onActions}
						className={`${look} text-left`}
					>
						<BubbleBody message={message} time={time} />
					</button>
				)}
			</div>
		</li>
	);
}

/** «Сьогодні» / «Вчора» / date between days. */
export function DaySeparator({ label }: { label: string }) {
	return (
		<li role="separator" aria-label={label} className="my-3 flex justify-center">
			<span className="bg-surface/80 text-caption text-text-muted rounded-full px-3 py-1 font-semibold">
				{label}
			</span>
		</li>
	);
}

/** A message on its way (optimistic) or one that failed, with a retry button. */
export function PendingBubble({
	message,
	onRetry,
}: {
	message: PendingMessage;
	onRetry: () => void;
}) {
	const { t } = useTranslation();
	const failed = message.status === 'failed';
	const text = message.quick === null ? message.body : t(`chat.status.${message.quick}`);
	return (
		<li className="mt-0.5 flex flex-col items-end gap-0.5">
			<p
				className={`${bubble} rounded-card rounded-br-sm bg-primary text-text ${failed ? 'opacity-50' : 'opacity-80'}`}
			>
				{text}
			</p>
			{failed ? (
				<button type="button" onClick={onRetry} className="text-caption text-danger min-h-11">
					{`${t('chat.failed')} · ${t('chat.retry')}`}
				</button>
			) : (
				<span className="text-caption text-text-muted">{t('chat.sending')}</span>
			)}
		</li>
	);
}
